/**
 * ProtectedRoute Component
 *
 * Wraps application content to require authentication.
 * Shows loading state while checking authentication.
 * Shows login prompt if user is not authenticated.
 * Automatically redirects to home on OAuth state mismatch errors.
 */

import React from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import LoadingIcon from './dasboardv3/components/Partials/LoadingIcon/LoadingIcon';

/**
 * ProtectedRoute Component
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactNode} Protected content or login prompt
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, error, isAuthenticated, login, oidcAuth } = useAuth();

  // Show the full-screen loader ONLY during the initial auth check (before any user
  // is loaded). Once a user exists, keep the app mounted even while `loading` /
  // `oidcAuth.isLoading` briefly flip true during a background silent token renew —
  // otherwise the entire tree unmounts and remounts on every renew, wiping all
  // in-memory state (live logs, selected home, fetched bonds). The renew re-fetches
  // /api/auth/me/ in AuthContext, which toggles bondUserLoading.
  if (!user && (loading || oidcAuth.isLoading)) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingIcon animation="fa-bounce"/>
        </div>
      </Container>
    );
  }

  // Show error message if authentication check failed
  if (error || oidcAuth.error) {
    const errorMessage = error || oidcAuth.error?.message || 'An unknown error occurred';

    // Handle "No matching state found in storage" error by redirecting to restart auth
    if (errorMessage.includes('No matching state found in storage')) {
      oidcAuth.removeUser().then(() => {
        window.location.href = '/';
      }).catch(() => {
        window.location.href = '/';
      });

      return (
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingIcon animation="fa-bounce"/>
          </div>
        </Container>
      );
    }

    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Authentication Error</Alert.Heading>
              <p>{errorMessage}</p>
              <hr />
              <div className="d-flex justify-content-end gap-2">
                <Button onClick={() => oidcAuth.removeUser()} variant="outline-secondary">
                  Clear Session
                </Button>
                <Button onClick={() => window.location.reload()} variant="outline-danger">
                  Retry
                </Button>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <Card.Body className="text-center p-5">
            <div className="mb-4">
              <LoadingIcon isLoading={false} />
            </div>
            <h2 className="mb-4">Bond Dashboard</h2>
            <Button
              variant="primary"
              size="lg"
              onClick={login}
              className="w-100"
            >
              Login
            </Button>
            {user && (
              <p className="mt-3 text-muted small">
                Welcome back, {user.email}
              </p>
            )}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // User is authenticated, render protected content
  return <>{children}</>;
};

export default ProtectedRoute;
